import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import type { Place } from '../types';
import { TYPE_GLYPH } from '../lib/glyphs';
import './MapCanvas.css';

interface Props {
    places: Place[];
    center: [number, number];
    selectedId?: string | undefined;
    userPosition?: { lat: number; lng: number } | undefined;
    onSelect: (place: Place) => void;
    /** Bottom inset so the sheet never covers the pin we just flew to. */
    bottomInset: number;
}

/**
 * Leaflet, driven imperatively.
 *
 * React-Leaflet would re-create layers on every render of a list this size; here
 * the map is created once and markers are diffed by hand, which keeps panning
 * smooth with 500+ pins.
 */
export function MapCanvas({ places, center, selectedId, userPosition, onSelect, bottomInset }: Props) {
    const hostRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const clusterRef = useRef<L.MarkerClusterGroup | null>(null);
    const markersRef = useRef(new Map<string, L.Marker>());
    const userRef = useRef<L.CircleMarker | null>(null);
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;

    // --- create once -------------------------------------------------------
    useEffect(() => {
        if (!hostRef.current || mapRef.current) return;

        const map = L.map(hostRef.current, {
            center,
            zoom: 13,
            // The sheet occupies the bottom of the screen, so Leaflet's own
            // chrome is disabled and our UI owns the edges. Attribution is
            // rendered in the sheet footer instead, where there is room for the
            // full ODbL and certifier credits.
            zoomControl: false,
            attributionControl: false,
        });

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            crossOrigin: true,
        }).addTo(map);

        L.control.zoom({ position: 'topright' }).addTo(map);

        const cluster = L.markerClusterGroup({
            maxClusterRadius: 56,
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: true,
            // Cluster bubbles are branded rather than Leaflet's default
            // green/yellow/red, which fought the palette and implied a severity
            // scale that does not exist.
            iconCreateFunction: (c) => {
                const n = c.getChildCount();
                const size = n < 10 ? 38 : n < 100 ? 46 : 54;
                return L.divIcon({
                    html: `<span>${n}</span>`,
                    className: 'hy-cluster',
                    iconSize: L.point(size, size),
                });
            },
        });
        map.addLayer(cluster);

        mapRef.current = map;
        clusterRef.current = cluster;

        return () => {
            map.remove();
            mapRef.current = null;
            clusterRef.current = null;
            markersRef.current.clear();
        };
        // Created once on mount; `center` is applied by the effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- markers ------------------------------------------------------------
    useEffect(() => {
        const cluster = clusterRef.current;
        if (!cluster) return;

        const wanted = new Set(places.map((p) => p.id));

        // Remove markers no longer in the filtered set.
        for (const [id, marker] of markersRef.current) {
            if (!wanted.has(id)) {
                cluster.removeLayer(marker);
                markersRef.current.delete(id);
            }
        }

        // Add the ones that are new.
        for (const place of places) {
            if (markersRef.current.has(place.id)) continue;
            const marker = L.marker([place.lat, place.lng], {
                icon: buildIcon(place),
                keyboard: true,
                title: place.name,
                alt: place.name,
            });
            marker.on('click', () => onSelectRef.current(place));
            marker.on('keypress', (e: L.LeafletKeyboardEvent) => {
                if (e.originalEvent.key === 'Enter' || e.originalEvent.key === ' ') onSelectRef.current(place);
            });
            cluster.addLayer(marker);
            markersRef.current.set(place.id, marker);
        }
    }, [places]);

    // --- selection ----------------------------------------------------------
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        for (const [id, marker] of markersRef.current) {
            const el = marker.getElement();
            el?.classList.toggle('is-selected', id === selectedId);
        }
        if (!selectedId) return;
        const place = places.find((p) => p.id === selectedId);
        if (!place) return;
        // Offset upward so the pin sits above the sheet rather than behind it.
        map.flyTo([place.lat, place.lng], Math.max(map.getZoom(), 16), { duration: 0.55, paddingBottomRight: [0, bottomInset] } as L.ZoomPanOptions);
    }, [selectedId, places, bottomInset]);

    // --- frame the city on arrival -----------------------------------------
    // A fixed centre and zoom opened Hong Kong on empty hillside with three pins
    // visible. Fitting to where the venues actually are means the first thing on
    // screen is the thing the user came for. Re-fits only when the city changes,
    // never on filtering, so the camera does not jump while you type.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        if (places.length === 0) {
            map.setView(center, 13, { animate: true });
            return;
        }
        const bounds = L.latLngBounds(places.map((p) => [p.lat, p.lng] as [number, number]));
        map.fitBounds(bounds, {
            paddingTopLeft: [28, 84],
            paddingBottomRight: [28, bottomInset + 28],
            maxZoom: 15,
            animate: true,
        });
        // Intentionally keyed on the city's centre, not on `places`: refitting
        // on every filter change would yank the map around mid-interaction.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [center, places.length > 0]);

    // --- the user's own position -------------------------------------------
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        userRef.current?.remove();
        userRef.current = null;
        if (!userPosition) return;
        userRef.current = L.circleMarker([userPosition.lat, userPosition.lng], {
            radius: 7,
            className: 'hy-userdot',
            interactive: false,
        }).addTo(map);
    }, [userPosition]);

    // Leaflet needs telling when its container resizes.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !hostRef.current) return;
        const ro = new ResizeObserver(() => map.invalidateSize());
        ro.observe(hostRef.current);
        return () => ro.disconnect();
    }, []);

    return <div ref={hostRef} className="map-canvas" role="application" aria-label="Map of halal places" />;
}

/**
 * Marker shape encodes trust, not just type:
 *   solid ring   = a certifier stands behind it
 *   dashed ring  = community or self-declared
 *   faded + slash= the certificate has lapsed or been withdrawn
 */
function buildIcon(place: Place): L.DivIcon {
    const strong = place.halal && ['certified', 'certified_kitchen'].includes(place.halal.status);
    const medium = place.halal && ['muslim_friendly', 'muslim_owned'].includes(place.halal.status);
    const dead = place.certState === 'expired' || place.certState === 'delisted';
    const cls = [
        'hy-pin',
        strong ? 'hy-pin--strong' : medium ? 'hy-pin--medium' : 'hy-pin--weak',
        dead ? 'hy-pin--dead' : '',
        place.precision === 'district' || place.precision === 'city' ? 'hy-pin--approx' : '',
    ].filter(Boolean).join(' ');

    return L.divIcon({
        className: cls,
        html: `<span class="hy-pin__glyph">${TYPE_GLYPH[place.type]}</span>`,
        iconSize: L.point(34, 34),
        iconAnchor: L.point(17, 17),
    });
}
